import axios from "axios";
import { IProspectionList, IProspectionListSummary, IProspectionListFilters, LeadStatus, ContactMethod } from "./prospectionList";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export const getProspectionLists = async (): Promise<IProspectionListSummary[]> => {
    const response = await axios.get(`${API_URL}/prospection-lists`, {
        headers: authHeaders(),
    });
    return response.data;
};

export interface IProspectionListCount {
    _id: string;
    totalCount: number;
    isCapped: boolean;
}

// One list's size. Deliberately separate from getProspectionLists: resolving a count
// runs the list's filters over every establishment, and bundling them meant no list
// could be shown until every list had been counted.
export const getProspectionListCount = async (id: string): Promise<IProspectionListCount> => {
    const response = await axios.get(`${API_URL}/prospection-lists/${id}/count`, {
        headers: authHeaders(),
    });
    return response.data;
};

export const getProspectionList = async (id: string): Promise<IProspectionList> => {
    const response = await axios.get(`${API_URL}/prospection-lists/${id}`, {
        headers: authHeaders(),
    });
    return response.data;
};

// The "Mes clients" list every user gets on signup — created on demand if missing.
export const getDefaultProspectionList = async (): Promise<IProspectionList> => {
    const response = await axios.get(`${API_URL}/prospection-lists/default`, {
        headers: authHeaders(),
    });
    return response.data;
};

export const createProspectionList = async (data: {
    name: string;
    description?: string;
    type: "static" | "dynamic";
    filters?: IProspectionListFilters;
    establishmentIds?: string[];
}): Promise<IProspectionList> => {
    const response = await axios.post(`${API_URL}/prospection-lists`, data, {
        headers: authHeaders(),
    });
    return response.data;
};

export const updateProspectionList = async (
    id: string,
    data: { name?: string; description?: string }
): Promise<IProspectionListSummary> => {
    const response = await axios.patch(`${API_URL}/prospection-lists/${id}`, data, {
        headers: authHeaders(),
    });
    return response.data;
};

export const updateLead = async (
    listId: string,
    establishmentId: string,
    data: {
        status?: LeadStatus;
        notes?: string;
        contactMethod?: ContactMethod;
    }
): Promise<void> => {
    await axios.patch(
        `${API_URL}/prospection-lists/${listId}/leads/${establishmentId}`,
        data,
        { headers: authHeaders() }
    );
};

export const deleteProspectionList = async (id: string): Promise<void> => {
    await axios.delete(`${API_URL}/prospection-lists/${id}`, {
        headers: authHeaders(),
    });
};

export const addEstablishmentToList = async (
    listId: string,
    establishmentId: string
): Promise<void> => {
    await axios.post(
        `${API_URL}/prospection-lists/${listId}/establishments/${establishmentId}`,
        {},
        { headers: authHeaders() }
    );
};

// Works for both the bulk-select action and the single-row + button (pass a one-element array).
export const addEstablishmentsToList = async (
    id: string,
    establishmentIds: string[]
): Promise<IProspectionListSummary> => {
    const response = await axios.post(
        `${API_URL}/prospection-lists/${id}/establishments`,
        { establishmentIds },
        { headers: authHeaders() }
    );
    return response.data;
};
